import {
  type AxiosHeaderValue,
  type AxiosResponseHeaders,
  type RawAxiosResponseHeaders,
} from "axios";

import type { BasePaginationData } from "shared/components/base/BasePagination";

const HEADER_NAMES = [
  "x-page",
  "x-per-page",
  "x-total-count",
  "x-total-pages",
] as const;

type PaginationHeaderName = (typeof HEADER_NAMES)[number];

export interface PaginationDataFromHeaders extends BasePaginationData {
  totalItems: number;
}

function getHeader(
  headers: AxiosResponseHeaders | RawAxiosResponseHeaders,
  name: PaginationHeaderName
): AxiosHeaderValue | undefined {
  if (typeof headers.get === "function") {
    return headers.get(name);
  }

  return Object.entries(headers).find(
    ([headerName]) => headerName.toLowerCase() === name
  )?.[1];
}

function parseHeaderValue(
  value: AxiosHeaderValue | undefined,
  name: PaginationHeaderName
): number {
  let numericValue = NaN;

  if (typeof value === "number") {
    numericValue = value;
  } else if (typeof value === "string" && /^\d+$/.test(value.trim())) {
    numericValue = Number(value);
  }

  if (!Number.isSafeInteger(numericValue) || numericValue < 0) {
    throw new Error(`Invalid pagination header: ${name}`);
  }

  return numericValue;
}

export function getPaginationDataFromHeaders(
  headers: AxiosResponseHeaders | RawAxiosResponseHeaders
): PaginationDataFromHeaders | undefined {
  const [page, pageSize, totalItems, pageCount] = HEADER_NAMES.map((name) =>
    getHeader(headers, name)
  );

  const headerValues = [page, pageSize, totalItems, pageCount];

  const missingHeaders = headerValues.filter(
    (value) => value === undefined || value === null
  );

  if (missingHeaders.length === HEADER_NAMES.length) {
    return undefined;
  }

  if (missingHeaders.length > 0) {
    throw new Error("Incomplete pagination headers");
  }

  return {
    page: parseHeaderValue(page, "x-page"),
    pageCount: parseHeaderValue(pageCount, "x-total-pages"),
    pageSize: parseHeaderValue(pageSize, "x-per-page"),
    totalItems: parseHeaderValue(totalItems, "x-total-count"),
  };
}
